import React, { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { BRAND } from '../config/branding';
import ComplianceDashboard from './ComplianceDashboard';
import UserManagement from './UserManagement';
import CompanySettings from './CompanySettings';
import './Settings.css';

function Settings() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [activeTab, setActiveTab] = useState('account');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [profileData, setProfileData] = useState({ full_name: user.full_name || '' });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [slaPricing, setSlaPricing] = useState(null);
  const [documentPricing, setDocumentPricing] = useState({
    manuals_free_limit: 5,
    manuals_price: 25.00,
    procedures_free_limit: 50,
    procedures_price: 1.50,
    forms_free_limit: 50,
    forms_price: 1.50,
    implementation_guides_free_limit: 26,
  });
  const [stripeSettings, setStripeSettings] = useState({
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    is_live_mode: false,
    currency: 'GBP',
    has_secret_key: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchCompanyInfo();
  }, []);

  const fetchSettings = async () => {
    try {
      // Try to fetch settings but handle failures gracefully
      let systemData = null;
      let slaData = null;

      try {
        const systemRes = await axiosClient.get('/settings/system');
        systemData = systemRes.data;
      } catch (err) {
        console.warn('System settings not available:', err.message);
        // Use default values
        systemData = {
          company_name: BRAND.name,
          company_email: 'admin@icscboss.com',
          company_website: 'https://icscboss.com',
          company_address: '',
          company_phone: '',
          password_min_length: 8,
          password_require_uppercase: true,
          password_require_lowercase: true,
          password_require_numbers: true,
          password_require_special: false,
          password_expiry_days: 90,
          session_timeout_minutes: 480,
          mfa_required_for_admins: false,
          audit_log_retention_days: 365,
          default_currency: 'GBP',
          currency_symbol: '£',
          default_tax_rate: 20,
          default_payment_terms_days: 30,
          certificate_expiry_warning_days: 90,
          default_certification_duration_months: 36,
          ticket_archive_after_days: 180,
          default_ticket_priority: 'Medium',
          smtp_from_email: 'noreply@icscboss.com',
          smtp_from_name: BRAND.name
        };
      }

      try {
        const slaRes = await axiosClient.get('/sla-settings/pricing');
        slaData = slaRes.data;
      } catch (err) {
        console.warn('SLA pricing not available:', err.message);
        // Use default values
        slaData = {
          iso_price_per_standard_first_3: 500,
          iso_price_4_to_10_total: 1200,
          cpd_certification_annual_fee: 350,
          company_assessments_annual_fee: 450
        };
      }

      setSettings(systemData);
      setSlaPricing(slaData);

      // Try to fetch document pricing (may not exist yet in backend)
      try {
        const docPricingRes = await axiosClient.get('/settings/document-pricing');
        if (docPricingRes.data) {
          setDocumentPricing(docPricingRes.data);
        }
      } catch (err) {
        console.log('Document pricing not available yet, using defaults');
      }

      // Try to fetch Stripe settings (system company only; backend returns 403 for others)
      try {
        const stripeRes = await axiosClient.get('/settings/stripe');
        if (stripeRes.data) {
          setStripeSettings(stripeRes.data);
        }
      } catch (err) {
        // 403 expected for non-system admins; other errors logged
        if (err.response?.status !== 403) {
          console.log('Stripe settings not available, using defaults');
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Some settings may not be available. Using default values.');
      setLoading(false);

      // Set defaults if everything fails
      if (!settings) {
        setSettings({
          company_name: BRAND.name,
          company_email: 'admin@icscboss.com',
          company_website: 'https://icscboss.com',
          company_address: '',
          company_phone: ''
        });
      }
      if (!slaPricing) {
        setSlaPricing({
          iso_price_per_standard_first_3: 500,
          iso_price_4_to_10_total: 1200
        });
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      // Save system settings
      await axiosClient.put('/settings/system', settings);

      setMessage('Settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save settings';
      setError(`Note: Backend endpoint may not be implemented yet. ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSLAPricingSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await axiosClient.put('/sla-settings/pricing', slaPricing);

      setMessage('SLA pricing settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save SLA pricing:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save SLA pricing settings';
      setError(`Note: Backend endpoint may not be implemented yet. ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateSLAPricing = (field, value) => {
    setSlaPricing(prev => ({ ...prev, [field]: value }));
  };

  const updateDocumentPricing = (field, value) => {
    setDocumentPricing(prev => ({ ...prev, [field]: value }));
  };

  const updateStripeSettings = (field, value) => {
    setStripeSettings(prev => ({ ...prev, [field]: value }));
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axiosClient.get(`/clients/${user.company_id}`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;

    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      const response = await axiosClient.post(
        `/clients/${user.company_id}/upload-logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMessage('Company logo uploaded successfully');
      setLogoFile(null);
      setLogoPreview(null);
      setTimeout(() => setMessage(''), 3000);

      // Refresh company info to display new logo
      if (response.data?.company) {
        setCompanyInfo(prev => ({ ...prev, company: response.data.company }));
      } else {
        fetchCompanyInfo();
      }
    } catch (error) {
      setError('Error uploading logo: ' + (error.response?.data?.detail || error.message));
      setTimeout(() => setError(''), 3000);
    }
  };

  const getProfessionalRoleName = (role) => {
    const roleMap = {
      'third_party_admin': 'Certification Administrator',
      'super_admin': 'System Administrator',
      'client': 'Client User'
    };
    return roleMap[role] || role?.replace('_', ' ');
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      await axiosClient.put(`/users/${user.id}`, profileData);

      setMessage('Profile updated successfully');
      const updatedUser = { ...user, full_name: profileData.full_name };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setIsEditingProfile(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setError('Error saving profile: ' + (error.response?.data?.detail || error.message));
      setTimeout(() => setError(''), 3000);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDocumentPricingSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await axiosClient.put('/settings/document-pricing', documentPricing);

      setMessage('ISO Document pricing settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to save document pricing:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save document pricing settings';
      setError(`Note: Backend endpoint may not be implemented yet. ${errorMsg}`);

      // Store in localStorage as fallback
      try {
        localStorage.setItem('icsc_boss_document_pricing', JSON.stringify(documentPricing));
        setMessage('Settings saved locally (backend endpoint pending)');
        setTimeout(() => setMessage(''), 3000);
      } catch (storageErr) {
        console.error('Failed to save to localStorage:', storageErr);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStripeSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      // Prepare payload - only include fields that have values
      const payload = {};
      if (stripeSettings.stripe_publishable_key) payload.stripe_publishable_key = stripeSettings.stripe_publishable_key;
      if (stripeSettings.stripe_secret_key) payload.stripe_secret_key = stripeSettings.stripe_secret_key;
      if (stripeSettings.stripe_webhook_secret) payload.stripe_webhook_secret = stripeSettings.stripe_webhook_secret;
      payload.is_live_mode = stripeSettings.is_live_mode;
      payload.currency = stripeSettings.currency;

      await axiosClient.put('/settings/stripe', payload);

      setMessage('Stripe settings saved successfully');
      setTimeout(() => setMessage(''), 3000);

      // Refresh settings to get updated has_secret_key flag
      const stripeRes = await axiosClient.get('/settings/stripe');
      if (stripeRes.data) {
        setStripeSettings(stripeRes.data);
      }
    } catch (err) {
      console.error('Failed to save Stripe settings:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save Stripe settings';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="settings-container"><p>Loading settings...</p></div>;
  }

  if (!settings) {
    return (
      <div className="settings-container">
        <h1>System Settings</h1>
        <div className="message error">
          Backend settings endpoints are not available. Please check your backend server.
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <h1>System Settings</h1>
      <p className="settings-subtitle">Configure {BRAND.name} application-wide settings</p>

      {error && (
        <div className="message error">
          {error}
          <br />
          <small>Some backend settings endpoints may not be fully implemented. The page will use default values for now.</small>
        </div>
      )}
      {message && <div className="message success">{message}</div>}

      <div className="settings-tabs">
        <button
          className={activeTab === 'account' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('account')}
        >
          Account Settings
        </button>
        <button
          className={activeTab === 'system' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('system')}
        >
          Company & System
        </button>
        <button
          className={activeTab === 'security' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
        <button
          className={activeTab === 'billing' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('billing')}
        >
          Billing & Currency
        </button>
        <button
          className={activeTab === 'document_pricing' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('document_pricing')}
        >
          ISO Doc Pricing
        </button>
        <button
          className={activeTab === 'certificates' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('certificates')}
        >
          Certificates
        </button>
        <button
          className={activeTab === 'sla_pricing' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('sla_pricing')}
        >
          SLA Pricing
        </button>
        <button
          className={activeTab === 'tickets' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('tickets')}
        >
          Tickets & Support
        </button>
        <button
          className={activeTab === 'email' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('email')}
        >
          Email
        </button>
        <button
          className={activeTab === 'compliance' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('compliance')}
        >
          Compliance
        </button>
        <button
          className={activeTab === 'users' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('users')}
        >
          User Management
        </button>
        {companyInfo?.company?.is_system && (
          <button
            className={activeTab === 'system_governance' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('system_governance')}
          >
            System Governance
          </button>
        )}
        {companyInfo?.company?.is_system && (
          <button
            className={activeTab === 'stripe' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('stripe')}
          >
            Stripe Payments
          </button>
        )}
      </div>

      <div className="settings-content">
        {/* Account Settings */}
        {activeTab === 'account' && (
          <div className="settings-section" style={{ maxWidth: '1000px' }}>
            <div style={{ marginBottom: '40px' }}>
              <h1 style={{ margin: 0, color: '#1a1a1a', fontSize: '32px', fontWeight: '700' }}>Settings</h1>
              <p style={{ color: '#666', marginTop: '8px', fontSize: '14px' }}>
                Manage your certification body account and company information
              </p>
            </div>

            {/* Account Information */}
            <div style={{
              background: 'white',
              border: '2px solid #d4af37',
              borderRadius: '8px',
              padding: '30px',
              marginBottom: '30px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                <h2 style={{ marginTop: 0, color: '#1a1a1a', fontSize: '20px', fontWeight: '700', margin: 0 }}>
                  Account Information
                </h2>
                {user.role !== 'client' && (
                  <button
                    onClick={() => {
                      if (isEditingProfile) {
                        setIsEditingProfile(false);
                        setProfileData({ full_name: user.full_name || '' });
                      } else {
                        setIsEditingProfile(true);
                      }
                    }}
                    style={{
                      padding: '10px 20px',
                      background: isEditingProfile ? '#999' : '#d4af37',
                      color: isEditingProfile ? 'white' : '#1a1a1a',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {isEditingProfile ? 'Cancel' : 'Edit Profile'}
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
                    Full Name
                  </label>
                  {isEditingProfile && user.role !== 'client' ? (
                    <input
                      type="text"
                      value={profileData.full_name}
                      onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1px solid #d4af37',
                        borderRadius: '4px',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
                      {user.full_name || 'Not set'}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
                    Email Address
                  </label>
                  <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
                    {user.email}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
                    Username
                  </label>
                  <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
                    {user.username}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>
                    Role
                  </label>
                  <div style={{ fontSize: '16px', color: '#1a1a1a', fontWeight: '500', padding: '12px', background: '#f8f8f8', borderRadius: '4px' }}>
                    {getProfessionalRoleName(user.role)}
                  </div>
                </div>
              </div>

              {isEditingProfile && user.role !== 'client' && (
                <div style={{ marginTop: '25px', paddingTop: '25px', borderTop: '1px solid #eee', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setIsEditingProfile(false);
                      setProfileData({ full_name: user.full_name || '' });
                    }}
                    style={{
                      padding: '10px 20px',
                      background: '#999',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    disabled={profileSaving}
                    style={{
                      padding: '10px 20px',
                      background: '#d4af37',
                      color: '#1a1a1a',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: profileSaving ? 'not-allowed' : 'pointer',
                      opacity: profileSaving ? 0.6 : 1
                    }}
                  >
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>

            {/* Company Settings — logo upload + company info fields */}
            <CompanySettings
              company={companyInfo?.company}
              logoPreview={logoPreview}
              logoFile={logoFile}
              onLogoChange={handleLogoChange}
              onLogoUpload={handleUploadLogo}
            />
          </div>
        )}

        {/* System & Company Settings */}
        {activeTab === 'system' && (
          <div className="settings-section">
            <h2>Company Information</h2>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={settings.company_name}
                onChange={(e) => updateSetting('company_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Company Address</label>
              <textarea
                value={settings.company_address || ''}
                onChange={(e) => updateSetting('company_address', e.target.value)}
                rows={3}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={settings.company_phone || ''}
                  onChange={(e) => updateSetting('company_phone', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={settings.company_email}
                  onChange={(e) => updateSetting('company_email', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                value={settings.company_website}
                onChange={(e) => updateSetting('company_website', e.target.value)}
              />
            </div>

            <h2 style={{ marginTop: '2rem' }}>Audit Settings</h2>
            <div className="form-group">
              <label>Audit Log Retention (days)</label>
              <input
                type="number"
                value={settings.audit_log_retention_days}
                onChange={(e) => updateSetting('audit_log_retention_days', parseInt(e.target.value))}
                min="30"
                max="3650"
              />
              <small>How long to keep audit log records (recommended: 365 days minimum for compliance)</small>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <div className="settings-section">
            <h2>Password Policy</h2>
            <div className="form-group">
              <label>Minimum Password Length</label>
              <input
                type="number"
                value={settings.password_min_length}
                onChange={(e) => updateSetting('password_min_length', parseInt(e.target.value))}
                min="6"
                max="20"
              />
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_uppercase}
                  onChange={(e) => updateSetting('password_require_uppercase', e.target.checked)}
                />
                Require uppercase letters
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_lowercase}
                  onChange={(e) => updateSetting('password_require_lowercase', e.target.checked)}
                />
                Require lowercase letters
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_numbers}
                  onChange={(e) => updateSetting('password_require_numbers', e.target.checked)}
                />
                Require numbers
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_special}
                  onChange={(e) => updateSetting('password_require_special', e.target.checked)}
                />
                Require special characters
              </label>
            </div>
            <div className="form-group">
              <label>Password Expiry (days)</label>
              <input
                type="number"
                value={settings.password_expiry_days}
                onChange={(e) => updateSetting('password_expiry_days', parseInt(e.target.value))}
                min="0"
                max="365"
              />
              <small>Set to 0 to disable password expiry</small>
            </div>

            <h2 style={{ marginTop: '2rem' }}>Session & MFA</h2>
            <div className="form-group">
              <label>Session Timeout (minutes)</label>
              <input
                type="number"
                value={settings.session_timeout_minutes}
                onChange={(e) => updateSetting('session_timeout_minutes', parseInt(e.target.value))}
                min="15"
                max="1440"
              />
              <small>Automatically log out inactive users (recommended: 480 minutes / 8 hours)</small>
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.mfa_required_for_admins}
                  onChange={(e) => updateSetting('mfa_required_for_admins', e.target.checked)}
                />
                Require MFA for admin accounts
              </label>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Billing & Currency Settings */}
        {activeTab === 'billing' && (
          <div className="settings-section">
            <h2>Currency & Tax</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Default Currency</label>
                <select
                  value={settings.default_currency}
                  onChange={(e) => updateSetting('default_currency', e.target.value)}
                >
                  <option value="GBP">GBP (British Pound)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Currency Symbol</label>
                <input
                  type="text"
                  value={settings.currency_symbol}
                  onChange={(e) => updateSetting('currency_symbol', e.target.value)}
                  maxLength={3}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Default Tax Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={settings.default_tax_rate}
                onChange={(e) => updateSetting('default_tax_rate', parseFloat(e.target.value))}
                min="0"
                max="100"
              />
              <small>E.g., 20 for UK VAT (20%), 0 for no tax</small>
            </div>
            <div className="form-group">
              <label>Default Payment Terms (days)</label>
              <input
                type="number"
                value={settings.default_payment_terms_days}
                onChange={(e) => updateSetting('default_payment_terms_days', parseInt(e.target.value))}
                min="0"
                max="365"
              />
              <small>Invoice payment due in X days (e.g., Net 30)</small>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Certificate Settings */}
        {activeTab === 'certificates' && (
          <div className="settings-section">
            <h2>Certificate Configuration</h2>
            <div className="form-group">
              <label>Expiry Warning Threshold (days)</label>
              <input
                type="number"
                value={settings.certificate_expiry_warning_days}
                onChange={(e) => updateSetting('certificate_expiry_warning_days', parseInt(e.target.value))}
                min="7"
                max="365"
              />
              <small>Send notifications when certificates expire within this many days</small>
            </div>
            <div className="form-group">
              <label>Default Certification Duration (months)</label>
              <input
                type="number"
                value={settings.default_certification_duration_months}
                onChange={(e) => updateSetting('default_certification_duration_months', parseInt(e.target.value))}
                min="12"
                max="60"
              />
              <small>Standard validity period for new certifications (typically 36 months / 3 years)</small>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* SLA Pricing Settings */}
        {activeTab === 'sla_pricing' && slaPricing && (
          <div className="settings-section">
            <h2>Accreditation SLA Pricing</h2>
            <p>Configure pricing for ISO standards accreditation and additional services.</p>

            <h3 style={{ marginTop: '1.5rem' }}>ISO Standards Pricing</h3>
            <div className="form-group">
              <label>Price per ISO Standard (First 3) - £</label>
              <input
                type="number"
                step="0.01"
                value={slaPricing.iso_price_per_standard_first_3}
                onChange={(e) => updateSLAPricing('iso_price_per_standard_first_3', parseFloat(e.target.value))}
                min="0"
              />
              <small>Price charged per ISO standard for the first 3 standards selected</small>
            </div>
            <div className="form-group">
              <label>Total Price for 4-10 ISO Standards - £</label>
              <input
                type="number"
                step="0.01"
                value={slaPricing.iso_price_4_to_10_total}
                onChange={(e) => updateSLAPricing('iso_price_4_to_10_total', parseFloat(e.target.value))}
                min="0"
              />
              <small>Flat rate when 4 or more ISO standards are selected</small>
            </div>

            <h3 style={{ marginTop: '2rem' }}>Additional Services (Annual Fees)</h3>
            <div className="form-group">
              <label>CPD Certification - £/year</label>
              <input
                type="number"
                step="0.01"
                value={slaPricing.cpd_certification_annual_fee}
                onChange={(e) => updateSLAPricing('cpd_certification_annual_fee', parseFloat(e.target.value))}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Company Assessments Licensing - £/year</label>
              <input
                type="number"
                step="0.01"
                value={slaPricing.company_assessments_annual_fee}
                onChange={(e) => updateSLAPricing('company_assessments_annual_fee', parseFloat(e.target.value))}
                min="0"
              />
            </div>

            <div className="form-actions">
              <button onClick={handleSLAPricingSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save SLA Pricing'}
              </button>
            </div>
          </div>
        )}

        {/* Tickets & Support Settings */}
        {activeTab === 'tickets' && (
          <div className="settings-section">
            <h2>Ticket Management</h2>
            <div className="form-group">
              <label>Auto-Archive Tickets After (days)</label>
              <input
                type="number"
                value={settings.ticket_archive_after_days}
                onChange={(e) => updateSetting('ticket_archive_after_days', parseInt(e.target.value))}
                min="30"
                max="3650"
              />
              <small>Automatically archive resolved tickets older than this (recommended: 180 days)</small>
            </div>
            <div className="form-group">
              <label>Default Ticket Priority</label>
              <select
                value={settings.default_ticket_priority}
                onChange={(e) => updateSetting('default_ticket_priority', e.target.value)}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Email Settings */}
        {activeTab === 'email' && (
          <div className="settings-section">
            <h2>Email Configuration</h2>
            <div className="form-group">
              <label>From Email Address</label>
              <input
                type="email"
                value={settings.smtp_from_email}
                onChange={(e) => updateSetting('smtp_from_email', e.target.value)}
              />
              <small>Email address used for system notifications</small>
            </div>
            <div className="form-group">
              <label>From Name</label>
              <input
                type="text"
                value={settings.smtp_from_name}
                onChange={(e) => updateSetting('smtp_from_name', e.target.value)}
              />
              <small>Display name for system emails</small>
            </div>

            <div className="info-box">
              <h4>SMTP Server Configuration</h4>
              <p>SMTP server settings (host, port, credentials) are configured in the backend .env file for security reasons.</p>
              <p>Contact your system administrator to update SMTP server settings.</p>
            </div>

            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* ISO Document Pricing Settings */}
        {activeTab === 'document_pricing' && (
          <div className="settings-section">
            <h2>ISO Document Generation Pricing</h2>
            <p>Configure pricing and free tier limits for Claude-powered ISO document generation.</p>

            <h3 style={{ marginTop: '1.5rem' }}>Manuals (Level-1 & Implementation)</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Free Tier Limit</label>
                <input
                  type="number"
                  value={documentPricing.manuals_free_limit}
                  onChange={(e) => updateDocumentPricing('manuals_free_limit', parseInt(e.target.value))}
                  min="0"
                />
                <small>Number of free manuals before charging</small>
              </div>
              <div className="form-group">
                <label>Price per Manual - £</label>
                <input
                  type="number"
                  step="0.01"
                  value={documentPricing.manuals_price}
                  onChange={(e) => updateDocumentPricing('manuals_price', parseFloat(e.target.value))}
                  min="0"
                />
                <small>Price charged after free tier</small>
              </div>
            </div>

            <h3 style={{ marginTop: '2rem' }}>Procedures (SOPs)</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Free Tier Limit</label>
                <input
                  type="number"
                  value={documentPricing.procedures_free_limit}
                  onChange={(e) => updateDocumentPricing('procedures_free_limit', parseInt(e.target.value))}
                  min="0"
                />
                <small>Number of free procedures before charging</small>
              </div>
              <div className="form-group">
                <label>Price per Procedure - £</label>
                <input
                  type="number"
                  step="0.01"
                  value={documentPricing.procedures_price}
                  onChange={(e) => updateDocumentPricing('procedures_price', parseFloat(e.target.value))}
                  min="0"
                />
                <small>Price charged after free tier</small>
              </div>
            </div>

            <h3 style={{ marginTop: '2rem' }}>Forms & Templates</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Free Tier Limit</label>
                <input
                  type="number"
                  value={documentPricing.forms_free_limit}
                  onChange={(e) => updateDocumentPricing('forms_free_limit', parseInt(e.target.value))}
                  min="0"
                />
                <small>Number of free forms before charging</small>
              </div>
              <div className="form-group">
                <label>Price per Form - £</label>
                <input
                  type="number"
                  step="0.01"
                  value={documentPricing.forms_price}
                  onChange={(e) => updateDocumentPricing('forms_price', parseFloat(e.target.value))}
                  min="0"
                />
                <small>Price charged after free tier</small>
              </div>
            </div>

            <h3 style={{ marginTop: '2rem' }}>Implementation Guides</h3>
            <div className="form-group">
              <label>Free Tier Limit</label>
              <input
                type="number"
                value={documentPricing.implementation_guides_free_limit}
                onChange={(e) => updateDocumentPricing('implementation_guides_free_limit', parseInt(e.target.value))}
                min="0"
              />
              <small>Number of free implementation guides (currently free indefinitely)</small>
            </div>

            <div className="info-box" style={{ marginTop: '1.5rem' }}>
              <h4> Important Notes</h4>
              <ul style={{ marginLeft: '20px' }}>
                <li>Super Admins always have unlimited FREE generation for all document types</li>
                <li>Changes apply immediately to all new document generations</li>
                <li>Existing usage and invoices are not affected</li>
                <li>Prices are in GBP (£) and exclude VAT</li>
              </ul>
            </div>

            <div className="form-actions">
              <button onClick={handleDocumentPricingSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save ISO Doc Pricing'}
              </button>
            </div>
          </div>
        )}

        {/* Compliance Dashboard Tab */}
        {activeTab === 'compliance' && (
          <div className="settings-section" style={{ padding: 0 }}>
            <ComplianceDashboard />
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="settings-section" style={{ padding: 0 }}>
            <UserManagement />
          </div>
        )}

        {/* ── System Governance Tab (ICSC system company only) ─────────── */}
        {activeTab === 'system_governance' && companyInfo?.company?.is_system && (
          <div className="settings-section">
            <h2>System Governance</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              Global platform defaults — changes apply across all certification bodies and clients.
            </p>

            {/* Global Branding */}
            <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px' }}>Global Branding</h3>
            <div className="form-group">
              <label>Platform Name</label>
              <input
                type="text"
                value={settings.company_name}
                onChange={(e) => updateSetting('company_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea
                value={settings.company_address || ''}
                onChange={(e) => updateSetting('company_address', e.target.value)}
                rows={3}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={settings.company_phone || ''}
                  onChange={(e) => updateSetting('company_phone', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={settings.company_email}
                  onChange={(e) => updateSetting('company_email', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                value={settings.company_website}
                onChange={(e) => updateSetting('company_website', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Audit Log Retention (days)</label>
              <input
                type="number"
                value={settings.audit_log_retention_days}
                onChange={(e) => updateSetting('audit_log_retention_days', parseInt(e.target.value))}
                min="30"
                max="3650"
              />
              <small>Recommended: 365 days minimum for compliance</small>
            </div>
            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Branding'}
              </button>
            </div>

            {/* Global Security Defaults */}
            <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px', marginTop: '2.5rem' }}>Global Security Defaults</h3>
            <div className="form-group">
              <label>Minimum Password Length</label>
              <input
                type="number"
                value={settings.password_min_length}
                onChange={(e) => updateSetting('password_min_length', parseInt(e.target.value))}
                min="6"
                max="20"
              />
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_uppercase}
                  onChange={(e) => updateSetting('password_require_uppercase', e.target.checked)}
                />
                Require uppercase letters
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_lowercase}
                  onChange={(e) => updateSetting('password_require_lowercase', e.target.checked)}
                />
                Require lowercase letters
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_numbers}
                  onChange={(e) => updateSetting('password_require_numbers', e.target.checked)}
                />
                Require numbers
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.password_require_special}
                  onChange={(e) => updateSetting('password_require_special', e.target.checked)}
                />
                Require special characters
              </label>
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label>Password Expiry (days)</label>
              <input
                type="number"
                value={settings.password_expiry_days}
                onChange={(e) => updateSetting('password_expiry_days', parseInt(e.target.value))}
                min="0"
                max="365"
              />
              <small>Set to 0 to disable password expiry</small>
            </div>
            <div className="form-group">
              <label>Session Timeout (minutes)</label>
              <input
                type="number"
                value={settings.session_timeout_minutes}
                onChange={(e) => updateSetting('session_timeout_minutes', parseInt(e.target.value))}
                min="15"
                max="1440"
              />
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.mfa_required_for_admins}
                  onChange={(e) => updateSetting('mfa_required_for_admins', e.target.checked)}
                />
                Require MFA for admin accounts
              </label>
            </div>
            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Security Defaults'}
              </button>
            </div>

            {/* Global Certificate Settings */}
            <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px', marginTop: '2.5rem' }}>Global Certificate Settings</h3>
            <div className="form-group">
              <label>Expiry Warning Threshold (days)</label>
              <input
                type="number"
                value={settings.certificate_expiry_warning_days}
                onChange={(e) => updateSetting('certificate_expiry_warning_days', parseInt(e.target.value))}
                min="7"
                max="365"
              />
              <small>Notify when certificates expire within this many days</small>
            </div>
            <div className="form-group">
              <label>Default Certification Duration (months)</label>
              <input
                type="number"
                value={settings.default_certification_duration_months}
                onChange={(e) => updateSetting('default_certification_duration_months', parseInt(e.target.value))}
                min="12"
                max="60"
              />
              <small>Standard validity period (typically 36 months)</small>
            </div>
            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Certificate Settings'}
              </button>
            </div>

            {/* Global SLA Settings */}
            {slaPricing && (
              <>
                <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px', marginTop: '2.5rem' }}>Global SLA Pricing</h3>
                <div className="form-group">
                  <label>Price per ISO Standard (First 3) — £</label>
                  <input
                    type="number"
                    step="0.01"
                    value={slaPricing.iso_price_per_standard_first_3}
                    onChange={(e) => updateSLAPricing('iso_price_per_standard_first_3', parseFloat(e.target.value))}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Total Price for 4–10 ISO Standards — £</label>
                  <input
                    type="number"
                    step="0.01"
                    value={slaPricing.iso_price_4_to_10_total}
                    onChange={(e) => updateSLAPricing('iso_price_4_to_10_total', parseFloat(e.target.value))}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>CPD Certification — £/year</label>
                  <input
                    type="number"
                    step="0.01"
                    value={slaPricing.cpd_certification_annual_fee}
                    onChange={(e) => updateSLAPricing('cpd_certification_annual_fee', parseFloat(e.target.value))}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Company Assessments Licensing — £/year</label>
                  <input
                    type="number"
                    step="0.01"
                    value={slaPricing.company_assessments_annual_fee}
                    onChange={(e) => updateSLAPricing('company_assessments_annual_fee', parseFloat(e.target.value))}
                    min="0"
                  />
                </div>
                <div className="form-actions">
                  <button onClick={handleSLAPricingSave} disabled={saving} className="btn-primary">
                    {saving ? 'Saving...' : 'Save SLA Pricing'}
                  </button>
                </div>
              </>
            )}

            {/* Global Document Pricing */}
            <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px', marginTop: '2.5rem' }}>Global Document Pricing</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Manuals — Free Tier Limit</label>
                <input
                  type="number"
                  value={documentPricing.manuals_free_limit}
                  onChange={(e) => updateDocumentPricing('manuals_free_limit', parseInt(e.target.value))}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Manuals — Price per Manual £</label>
                <input
                  type="number"
                  step="0.01"
                  value={documentPricing.manuals_price}
                  onChange={(e) => updateDocumentPricing('manuals_price', parseFloat(e.target.value))}
                  min="0"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Procedures — Free Tier Limit</label>
                <input
                  type="number"
                  value={documentPricing.procedures_free_limit}
                  onChange={(e) => updateDocumentPricing('procedures_free_limit', parseInt(e.target.value))}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Procedures — Price Each £</label>
                <input
                  type="number"
                  step="0.01"
                  value={documentPricing.procedures_price}
                  onChange={(e) => updateDocumentPricing('procedures_price', parseFloat(e.target.value))}
                  min="0"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Forms — Free Tier Limit</label>
                <input
                  type="number"
                  value={documentPricing.forms_free_limit}
                  onChange={(e) => updateDocumentPricing('forms_free_limit', parseInt(e.target.value))}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Forms — Price Each £</label>
                <input
                  type="number"
                  step="0.01"
                  value={documentPricing.forms_price}
                  onChange={(e) => updateDocumentPricing('forms_price', parseFloat(e.target.value))}
                  min="0"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Implementation Guides — Free Tier Limit</label>
              <input
                type="number"
                value={documentPricing.implementation_guides_free_limit}
                onChange={(e) => updateDocumentPricing('implementation_guides_free_limit', parseInt(e.target.value))}
                min="0"
              />
            </div>
            <div className="form-actions">
              <button onClick={handleDocumentPricingSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Document Pricing'}
              </button>
            </div>

            {/* Accreditation Governance Settings */}
            <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px', marginTop: '2.5rem' }}>Accreditation Governance Settings</h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '1rem' }}>
              Controls how ICSC accreditations for certification bodies are managed platform-wide.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label>Accreditation Duration (months)</label>
                <input
                  type="number"
                  value={settings.accreditation_duration_months ?? 36}
                  onChange={(e) => updateSetting('accreditation_duration_months', parseInt(e.target.value))}
                  min="12"
                  max="120"
                />
                <small>Default validity period for new accreditations (typically 36 months)</small>
              </div>
              <div className="form-group">
                <label>Expiry Warning (days)</label>
                <input
                  type="number"
                  value={settings.accreditation_expiry_warning_days ?? 90}
                  onChange={(e) => updateSetting('accreditation_expiry_warning_days', parseInt(e.target.value))}
                  min="7"
                  max="365"
                />
                <small>Notify when accreditation expires within this many days</small>
              </div>
            </div>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.accreditation_evidence_required ?? false}
                  onChange={(e) => updateSetting('accreditation_evidence_required', e.target.checked)}
                />
                Evidence document required before approval
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.accreditation_auto_expire ?? true}
                  onChange={(e) => updateSetting('accreditation_auto_expire', e.target.checked)}
                />
                Auto-expire accreditations past their expiry date (checked at certificate issuance)
              </label>
            </div>
            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Accreditation Governance'}
              </button>
            </div>

            {/* Global Email Defaults */}
            <h3 style={{ borderBottom: '2px solid #d4af37', paddingBottom: '8px', marginTop: '2.5rem' }}>Global Email Defaults</h3>
            <div className="form-group">
              <label>From Email Address</label>
              <input
                type="email"
                value={settings.smtp_from_email}
                onChange={(e) => updateSetting('smtp_from_email', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>From Name</label>
              <input
                type="text"
                value={settings.smtp_from_name}
                onChange={(e) => updateSetting('smtp_from_name', e.target.value)}
              />
            </div>
            <div className="info-box">
              <h4>SMTP Server Configuration</h4>
              <p>SMTP server settings (host, port, credentials) are configured in the backend .env file for security reasons.</p>
            </div>
            <div className="form-actions">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Email Defaults'}
              </button>
            </div>
          </div>
        )}

        {/* Stripe Payments Tab */}
        {activeTab === 'stripe' && companyInfo?.company?.is_system && (
          <div className="settings-section">
            <h2>Stripe Payment Integration</h2>
            <p>Configure Stripe API credentials for payment processing. All certification body payments to ICSC will be processed through your Stripe account.</p>

            <div className="info-box" style={{ marginBottom: '20px', background: '#e3f2fd', padding: '15px', borderRadius: '8px', border: '1px solid #2196f3' }}>
              <h4 style={{ marginTop: 0, color: '#1976d2' }}>Security Notice</h4>
              <ul style={{ marginLeft: '20px', marginBottom: 0 }}>
                <li>Your Stripe Secret Key is encrypted and never displayed after saving</li>
                <li>All payment data is processed securely through Stripe's PCI-compliant infrastructure</li>
                <li>Test mode allows you to test payments without processing real transactions</li>
                <li>Get your API keys from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>Stripe Dashboard</a></li>
              </ul>
            </div>

            <div className="form-group">
              <label>Environment Mode</label>
              <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={!stripeSettings.is_live_mode}
                    onChange={() => updateStripeSettings('is_live_mode', false)}
                    style={{ marginRight: '8px' }}
                  />
                  Test Mode (Recommended for setup)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={stripeSettings.is_live_mode}
                    onChange={() => updateStripeSettings('is_live_mode', true)}
                    style={{ marginRight: '8px' }}
                  />
                  Live Mode (Production)
                </label>
              </div>
              <small>Start with Test Mode to verify your integration before going live</small>
            </div>

            <div className="form-group">
              <label>Stripe Publishable Key</label>
              <input
                type="text"
                value={stripeSettings.stripe_publishable_key || ''}
                onChange={(e) => updateStripeSettings('stripe_publishable_key', e.target.value)}
                placeholder={stripeSettings.is_live_mode ? "pk_live_..." : "pk_test_..."}
              />
              <small>Starts with {stripeSettings.is_live_mode ? 'pk_live_' : 'pk_test_'} - Safe to use in your frontend</small>
            </div>

            <div className="form-group">
              <label>Stripe Secret Key</label>
              <input
                type="password"
                value={stripeSettings.stripe_secret_key || ''}
                onChange={(e) => updateStripeSettings('stripe_secret_key', e.target.value)}
                placeholder={stripeSettings.has_secret_key ? "••••••••••••••••" : (stripeSettings.is_live_mode ? "sk_live_..." : "sk_test_...")}
              />
              <small>
                Starts with {stripeSettings.is_live_mode ? 'sk_live_' : 'sk_test_'} -
                {stripeSettings.has_secret_key ? ' Currently set (enter new value to update)' : ' Not yet configured'}
              </small>
            </div>

            <div className="form-group">
              <label>Stripe Webhook Secret</label>
              <input
                type="password"
                value={stripeSettings.stripe_webhook_secret || ''}
                onChange={(e) => updateStripeSettings('stripe_webhook_secret', e.target.value)}
                placeholder="whsec_..."
              />
              <small>Used to verify webhook signatures from Stripe. Get this from your Stripe webhook settings.</small>
            </div>

            <div className="form-group">
              <label>Default Currency</label>
              <select
                value={stripeSettings.currency}
                onChange={(e) => updateStripeSettings('currency', e.target.value)}
              >
                <option value="GBP">GBP (British Pound)</option>
                <option value="USD">USD (US Dollar)</option>
                <option value="EUR">EUR (Euro)</option>
              </select>
              <small>Default currency for Stripe payment links</small>
            </div>

            <div className="info-box" style={{ marginTop: '20px', background: '#fff3cd', padding: '15px', borderRadius: '8px', border: '1px solid #ffc107' }}>
              <h4 style={{ marginTop: 0, color: '#856404' }}>Next Steps After Configuration</h4>
              <ol style={{ marginLeft: '20px', marginBottom: 0 }}>
                <li>Save your Stripe API credentials</li>
                <li>Set up webhook endpoint in Stripe Dashboard: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '4px' }}>{window.location.origin}/stripe/webhook</code></li>
                <li>Select these events: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '4px' }}>payment_intent.succeeded</code>, <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '4px' }}>payment_intent.payment_failed</code></li>
                <li>Copy the webhook signing secret and save it above</li>
                <li>Test with a certification body invoice payment</li>
              </ol>
            </div>

            <div className="form-actions">
              <button onClick={handleStripeSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : 'Save Stripe Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;
